import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { CampaignApiRecord } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { campaignDetailQueryKey } from "./campaignQueryCache";
import { retryOnTransientErrors } from "./queryUtils";
import { unwrapGamePassEntityOrNull } from "./responseMappers";

const buildGetCampaignUrl = (campaignId: string): string =>
  API_ROUTES.GET_CAMPAIGN.replace("{campaign_id}", campaignId);

/**
 * Fetcher for `GET /gamepass/v2/admin/campaigns/{campaign_id}` — the single
 * record that backs the Monthly Details page. Tolerant unwrap: a `2xx` body
 * that carries no recognisable record resolves to `null` (the page renders its
 * not-found state) rather than throwing, since a `404` already surfaces as an
 * axios error the query treats as `isError`.
 */
export const fetchCampaign = async (
  campaignId: string,
): Promise<CampaignApiRecord | null> => {
  const response = await getPrivateInstance().get<object>(
    buildGetCampaignUrl(campaignId),
  );
  return unwrapGamePassEntityOrNull<CampaignApiRecord>(
    response.data,
    "campaign",
  );
};

/**
 * React Query hook for a single campaign by id. Disabled until an id is
 * available so the page doesn't fire a request with an empty path segment
 * while the route is still resolving. Mirrors the seasons-side list hooks —
 * 30s stale time, transient-error retry.
 */
export const useGetCampaign = (
  campaignId?: string,
): UseQueryResult<CampaignApiRecord | null, Error> =>
  useQuery<CampaignApiRecord | null, Error>({
    queryKey: campaignDetailQueryKey(campaignId),
    queryFn: () => fetchCampaign(campaignId as string),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
    enabled: Boolean(campaignId),
  });
